import { useState } from 'react';
import type { BasicHealthData } from '../../types/health';

interface OnboardingStep2Props {
  onComplete: (data: Partial<BasicHealthData>) => void;
  onBack: () => void;
}

export default function OnboardingStep2({ onComplete, onBack }: OnboardingStep2Props) {
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [unit, setUnit] = useState<'metric' | 'imperial'>('metric');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    let heightInCm = parseFloat(height);
    let weightInKg = parseFloat(weight);

    if (unit === 'imperial') {
      heightInCm = parseFloat(height) * 2.54; // inches to cm
      weightInKg = parseFloat(weight) * 0.453592; // lbs to kg
    }

    if (heightInCm && weightInKg) {
      onComplete({ height: heightInCm, weight: weightInKg });
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
          <i className="ri-ruler-line text-3xl text-teal-600"></i>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Your Measurements</h2>
        <p className="text-lg text-gray-600">This helps us calculate your metabolic health baseline</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="flex justify-center mb-6">
          <div className="inline-flex rounded-lg border-2 border-gray-200 p-1">
            <button
              type="button"
              onClick={() => setUnit('metric')}
              className={`px-6 py-2 rounded-md transition-all whitespace-nowrap ${
                unit === 'metric'
                  ? 'bg-teal-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Metric
            </button>
            <button
              type="button"
              onClick={() => setUnit('imperial')}
              className={`px-6 py-2 rounded-md transition-all whitespace-nowrap ${
                unit === 'imperial'
                  ? 'bg-teal-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Imperial
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Height {unit === 'metric' ? '(cm)' : '(inches)'}
            </label>
            <input
              type="number"
              step="0.1"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder={unit === 'metric' ? '170' : '67'}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none text-lg"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Weight {unit === 'metric' ? '(kg)' : '(lbs)'}
            </label>
            <input
              type="number"
              step="0.1"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder={unit === 'metric' ? '70' : '154'}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none text-lg"
              required
            />
          </div>
        </div>

        <div className="flex justify-between pt-6">
          <button
            type="button"
            onClick={onBack}
            className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-gray-400 transition-colors whitespace-nowrap"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={!height || !weight}
            className="px-8 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}
